/**
 * Usage figures for the SELECTED area, and only for it (RESP2c).
 *
 * The rule this exists to enforce: one request per selection, never one
 * per row. A thirteen-row table that asked for usage per row would make
 * a settings page cost thirteen round trips to render, and every one of
 * them would be thrown away the moment the user clicked something.
 *
 * The list response already carries the three operational numbers the
 * table needs. This endpoint adds the ALL-TIME totals, which are what
 * decide whether an area can be deleted — and those are only ever needed
 * for the one area a person is looking at.
 *
 * Race protection is a generation counter rather than AbortController,
 * matching `useResponsibilityAreas`: the risk is a stale response for a
 * previously selected area landing after a newer one, and ignoring it by
 * generation is immune to a fetch that already resolved.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchResponsibilityAreaUsage } from "../../../api/adminData";

export default function useAreaUsage(areaId) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const generation = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!areaId) {
      setUsage(null);
      setError(null);
      return;
    }
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    // Cleared on purpose: showing the previous area's totals under the
    // new area's name is worse than showing a skeleton, because it reads
    // as data rather than as "not loaded yet".
    setUsage(null);
    try {
      const data = await fetchResponsibilityAreaUsage(areaId);
      if (gen !== generation.current || !mounted.current) return;
      setUsage(data || null);
    } catch (err) {
      if (gen !== generation.current || !mounted.current) return;
      setError(err?.message || "Verwendung konnte nicht geladen werden.");
    } finally {
      if (gen === generation.current && mounted.current) setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { usage, loading, error, refresh: load };
}
